import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { useMessage } from '@tmapy/intl';
import { Icon } from '@tmapy/style-guide';

import type { NestedPriceListItemResponse } from '../../openapi/types/NestedPriceListItem';
import {
  priceListMessages,
  priceListItemTypeMessages,
} from '../../priceList/components/priceListMessages';
import { getPriceBandRange, isPriceBandFree } from '../../priceList/utils';

import { SvgCart } from './SvgCart';

const msg = defineMessages({
  tooltip: {
    id: 'publishset.priceIcon.tooltip',
    defaultMessage: 'Zpoplatněno podle ceníku: {name} ({type}), DPH {vatRate, number} %',
  },
  bandLine: {
    id: 'publishset.priceIcon.bandLine',
    defaultMessage: '{range}: {price}',
  },
  tooltipNameOnly: {
    id: 'publishset.priceIcon.tooltipNameOnly',
    defaultMessage: 'Zpoplatněno podle ceníku: {name}',
  },
});

type PriceIconProps = {
  /** Vnořená položka z přehledu sad — nese typ, sazbu DPH i pásma. */
  priceListItem?: NestedPriceListItemResponse | null;
  /**
   * Detail žádosti vrací u řádku jen název položky, ne celý objekt. Tooltip se
   * pak omezí na název, aby se kvůli pásmům nedotahovala další odpověď.
   */
  priceListItemName?: string | null;
};

export const PriceIcon: React.FC<PriceIconProps> = ({ priceListItem, priceListItemName }) => {
  const intl = useIntl();
  const formatMessage = useMessage();

  const buildFullTooltip = (item: NestedPriceListItemResponse) => {
    const typeLabel = formatMessage.lookup(priceListItemTypeMessages, item.type) ?? item.type;

    // Pásma jen vypisujeme — cenu žádosti počítá vždy backend.
    const bandLines = (item.bands ?? []).map((band) => {
      const range = getPriceBandRange(band);

      return intl.formatMessage(msg.bandLine, {
        range: intl.formatMessage(range.descriptor, range.values),
        price: isPriceBandFree(band)
          ? intl.formatMessage(priceListMessages.bandsPriceFree)
          : intl.formatMessage(priceListMessages.bandsPriceCzk, { price: Number(band.price) }),
      });
    });

    return [
      intl.formatMessage(msg.tooltip, {
        name: item.name,
        type: typeLabel,
        vatRate: Number(item.vat_rate),
      }),
      ...bandLines,
    ].join('\n');
  };

  const tooltip = priceListItem
    ? buildFullTooltip(priceListItem)
    : intl.formatMessage(msg.tooltipNameOnly, { name: priceListItemName });

  return (
    <span className='sg-a-d-f sg-u-c-body-darkest'>
      <Icon element={<SvgCart />} tooltip={tooltip} />
    </span>
  );
};
